// Affiliate programs: AFFCatalog, WhoAff and the programs' own sites, each
// source kept with its own terms and date. A filter must hold inside one
// source's claim — terms are never stitched together across sources.
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { getRadarAffiliates, type AffiliateProgram } from '@/api/radar';
import {
  AFFILIATE_FIELD_FILTERS, EMPTY_AFFILIATE_FILTERS, downloadJson, hostOf, matchesAffiliate, ru, safeHttps,
  type AffiliateFilters,
} from './logic';
import { LoadError, Loading, Pager, SelectInput, TextInput } from './controls';

const PAGE_SIZE = 12;
const RATE_KEYS = ['CPA', 'RevShare', 'Hybrid', 'Caps'];
const KIND_LABEL: Record<string, string> = {
  program: 'Партнёрская программа', network: 'Партнёрская сеть', advertiser: 'Прямой рекламодатель',
};

export function AffiliatesTab() {
  const data = useQuery({ queryKey: ['radar', 'affiliates'], queryFn: getRadarAffiliates, staleTime: Infinity });
  const [f, setF] = useState<AffiliateFilters>(EMPTY_AFFILIATE_FILTERS);
  const [page, setPage] = useState(1);
  const topRef = useRef<HTMLDivElement>(null);

  const programs = useMemo(() => data.data?.programs ?? [], [data.data]);
  const options = useMemo(() => {
    const claims = programs.flatMap((p) => p.claims);
    const uniq = (values: (string | undefined | null)[]) => [...new Set(values.filter((v): v is string => !!v))].sort();
    return {
      geo: uniq(claims.flatMap((c) => c.geo?.target ?? [])),
      source: uniq(claims.map((c) => hostOf(c.source_url))),
      vertical: uniq(claims.flatMap((c) => c.verticals ?? [])),
      traffic: uniq(claims.flatMap((c) => c.traffic ?? [])),
      ...Object.fromEntries(AFFILIATE_FIELD_FILTERS.map(([k, field]) => [k, uniq(claims.map((c) => c.fields?.[field]))])) as Record<(typeof AFFILIATE_FIELD_FILTERS)[number][0], string[]>,
    };
  }, [programs]);
  const filtered = useMemo(() => programs.filter((p) => matchesAffiliate(p, f)), [programs, f]);
  const official = useMemo(() => programs.filter((p) => p.claims.some((c) => c.verification === 'official_site')).length, [programs]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);

  const set = <K extends keyof AffiliateFilters>(key: K, value: AffiliateFilters[K]) => { setF((x) => ({ ...x, [key]: value })); setPage(1); };

  if (data.isLoading) return <Loading what="партнёрские программы" />;
  if (data.error) return <LoadError error={data.error} />;

  return (
    <div className="space-y-5">
      <p className="text-sm text-content-muted max-w-4xl">
        GEO, комиссии и условия из публичных источников. Официальный сайт подтверждает публикацию условий, а не согласование оффера для вашего трафика.
        «До» — рекламный максимум; отсутствие caps означает «не опубликованы». Связка бренд + GEO + трафик + ставка требует отдельного подтверждения.
      </p>

      <section className="bg-surface border border-border rounded-lg p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr_auto] items-end">
        <TextInput label="Программа или бренд" type="search" value={f.q} onChange={(v) => set('q', v)} placeholder="V.Partners, 888STARZ…" />
        <SelectInput label="Целевой GEO" value={f.geo} onChange={(v) => set('geo', v)} all="Все страны" options={options.geo} />
        <SelectInput label="Доказательство" value={f.proof} onChange={(v) => set('proof', v)} all="Все источники" options={[['official_site', 'Есть официальный источник'], ['directory_claim', 'Только каталоги']]} />
        <SelectInput label="Модель" value={f.model} onChange={(v) => set('model', v)} all="Все" options={['CPA', 'RevShare', 'Hybrid']} />
        <Button variant="secondary" onClick={() => downloadJson('affiliate-programs.json', filtered)}>↓ JSON</Button>
      </section>

      <details open className="bg-surface border border-border rounded-lg p-4">
        <summary className="cursor-pointer font-medium text-content">Дополнительные фильтры</summary>
        <div className="grid gap-3 mt-3 sm:grid-cols-2 lg:grid-cols-4">
          <SelectInput label="Тип записи" value={f.kind} onChange={(v) => set('kind', v)} all="Все" options={Object.entries(KIND_LABEL)} />
          <SelectInput label="Источник" value={f.source} onChange={(v) => set('source', v)} all="Все" options={options.source} />
          <SelectInput label="Вертикаль" value={f.vertical} onChange={(v) => set('vertical', v)} all="Все" options={options.vertical} />
          <SelectInput label="Источник трафика" value={f.traffic} onChange={(v) => set('traffic', v)} all="Все" options={options.traffic} />
          <SelectInput label="Лицензия по источнику" value={f.license} onChange={(v) => set('license', v)} all="Все" options={options.license} />
          <SelectInput label="Частота выплат" value={f.frequency} onChange={(v) => set('frequency', v)} all="Все" options={options.frequency} />
          <SelectInput label="Negative carryover" value={f.negative} onChange={(v) => set('negative', v)} all="Все" options={options.negative} />
          <SelectInput label="Admin Fee" value={f.fee} onChange={(v) => set('fee', v)} all="Все" options={options.fee} />
          <SelectInput label="Caps опубликованы" value={f.caps} onChange={(v) => set('caps', v)} all="Все" options={[['yes', 'Да'], ['no', 'Не опубликованы']]} />
          <SelectInput label="Целевые GEO указаны" value={f.known} onChange={(v) => set('known', v)} all="Все" options={[['yes', 'Да'], ['no', 'Нет']]} />
          <SelectInput label="Конфликт GEO" value={f.conflict} onChange={(v) => set('conflict', v)} all="Все" options={[['yes', 'Есть'], ['no', 'Не найден']]} />
          <TextInput label="RS: заявленный процент от" type="number" min={0} max={100} value={f.rs} onChange={(v) => set('rs', v)} placeholder="Например, 40" />
          <SelectInput label="Валюта CPA" value={f.currency} onChange={(v) => set('currency', v)} all="Все" options={['EUR', 'USD', 'GBP']} />
          <TextInput label="CPA: заявленный максимум от" type="number" min={0} value={f.cpa} onChange={(v) => set('cpa', v)} placeholder={f.currency ? 'Сумма' : 'Сначала выберите валюту'} disabled={!f.currency} />
          <div className="self-end">
            <Button variant="secondary" onClick={() => { setF(EMPTY_AFFILIATE_FILTERS); setPage(1); }}>Сбросить фильтры</Button>
          </div>
        </div>
        <p className="text-xs text-content-subtle mt-3">
          Условия фильтров должны совпадать в одном источнике. Пороги CPA и RS сравнивают опубликованные значения, включая максимум «до», а не гарантированную ставку.
          CPA сравнивается только внутри выбранной валюты.
        </p>
      </details>

      <p ref={topRef} role="status" className="text-sm text-content-muted scroll-mt-4">
        {filtered.length} из {programs.length} программ · {official} с официальными источниками
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        {filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE).map((p) => <ProgramCard key={p.id} program={p} />)}
      </div>
      {!filtered.length && <p className="text-sm text-content-subtle">Нет программ по выбранным фильтрам.</p>}
      <Pager page={current} pages={pages} onPage={(n) => { setPage(n); topRef.current?.scrollIntoView({ behavior: 'smooth' }); }} />
    </div>
  );
}

function ProgramCard({ program: p }: { program: AffiliateProgram }) {
  return (
    <article className="bg-surface border border-border rounded-lg p-4 space-y-3 min-w-0">
      <div>
        <h3 className="font-medium text-content text-lg break-words">{p.name}</h3>
        <span className="text-xs text-content-subtle">{KIND_LABEL[p.kind] ?? p.kind}</span>
        {p.website && (
          <a href={safeHttps(p.website)} target="_blank" rel="noreferrer" className="block text-sm text-primary hover:underline">Сайт программы ↗</a>
        )}
      </div>
      {p.claims.map((c) => (
        <section key={c.source_url} className="border-t border-border pt-3 space-y-2 text-sm">
          <div>
            <strong className={c.verification === 'official_site' ? 'text-success' : 'text-content'}>
              {c.verification === 'official_site' ? '● Официальный сайт' : '◇ Данные каталога'}
            </strong>
            <p className="text-xs text-content-subtle">
              {ru(c.observed_at)}{c.source_updated ? ` · обновление источника: ${c.source_updated}` : ''}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {RATE_KEYS.map((k) => (
              <div key={k} className="bg-surface-muted rounded-md px-2 py-1.5">
                <p className="text-[11px] text-content-subtle">{k}</p>
                <p className="font-medium text-content break-words">{c.fields?.[k] || 'Не опубликовано'}</p>
              </div>
            ))}
          </div>
          <p><span className="font-medium">Целевые GEO:</span> {c.geo?.target?.join(', ') || 'Не подтверждены этим источником'}</p>
          {!!c.conflicts?.length && (
            <p className="text-warning">Конфликт GEO: {c.conflicts.join(', ')} — одновременно целевые и запрещённые</p>
          )}
          <details>
            <summary className="cursor-pointer text-content-muted">Ограничения и дополнительные сведения</summary>
            <div className="mt-2 space-y-1">
              <p><span className="font-medium">Запрещённые GEO:</span> {c.geo?.forbidden?.join(', ') || 'Не опубликованы'}</p>
              {Object.entries(c.fields ?? {}).filter(([k]) => !RATE_KEYS.includes(k)).map(([k, v]) => (
                <p key={k}><span className="font-medium">{k}:</span> {v}</p>
              ))}
              {c.overview && <p className="text-content-muted">{c.overview}</p>}
            </div>
          </details>
          <a href={safeHttps(c.source_url)} target="_blank" rel="noreferrer" className="text-primary hover:underline">Источник ↗</a>
        </section>
      ))}
    </article>
  );
}
